import React, { useState } from "react";
import axios from "axios";
import "./UpdateDelivery.css";

const UpdateDelivery = () => {
  const [reportDate, setReportDate] = useState("");
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });

  // ✅ Fetch bookings
  const fetchBookings = async (clearMsg = true) => {
    if (!reportDate) {
      setMessage({ text: "Please select a date", type: "error" });
      return;
    }

    setBookings([]);
    setLoading(true);
    if (clearMsg) setMessage({ text: "", type: "" });

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/get_bookings`,
        { date: reportDate }
      );

      if (res.data.status === "success") {
        const cleanData = res.data.data.map((b) => ({
          ...b,
          Delivery_Status: b.Delivery_Status?.trim() || "",
          Actual_Delivery_Date: b.Actual_Delivery_Date || "",
          Remarks: b.Remarks || "", // ✅ new field
        }));
        setBookings(cleanData);
      } else {
        setBookings([]);
        setMessage({
          text: res.data.message || "No data found",
          type: "error",
        });
      }
    } catch (err) {
      console.error(err);
      setBookings([]);
      setMessage({ text: "Error fetching data", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  // ✅ Handle field changes (status/date/remarks)
  const handleChange = (index, field, value) => {
    const updated = [...bookings];
    updated[index][field] = value;

    if (field === "Delivery_Status") {
      const today = new Date().toISOString().split("T")[0];
      if (value === "Cancelled" || value === "Delivered") {
        updated[index]["Actual_Delivery_Date"] = today;
      } else if (value === "Pending" || !value.trim()) {
        updated[index]["Actual_Delivery_Date"] = "";
      }
    }

    setBookings(updated);
  };

  // ✅ Save booking updates
  const handleUpdate = async (e, booking) => {
    e.preventDefault();

    if (!booking.Villa_No || !booking.Booking_Day) {
      setMessage({ text: "Invalid booking data", type: "error" });
      return;
    }

    setSaving(true);
    setMessage({ text: "", type: "" });

    try {
      const postData = {
        booking_no: booking.Booking_No || null,
        villa_no: booking.Villa_No,
        booking_date: booking.Booking_Day,
        actual_delivery_date: booking.Actual_Delivery_Date,
        delivery_status: booking.Delivery_Status,
        remarks: booking.Remarks || "", // ✅ include remarks
      };

      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/update_booking`,
        postData
      );

      if (res.data.status === "success") {
        await fetchBookings(false);
        setMessage({
          text: `Villa ${booking.Villa_No} updated successfully.`,
          type: "success",
        });
      } else {
        setMessage({
          text: `Failed to update Villa ${booking.Villa_No}. ${
            res.data.message || ""
          }`,
          type: "error",
        });
      }
    } catch (err) {
      console.error(err);
      setMessage({
        text: `Error updating Villa ${booking.Villa_No}.`,
        type: "error",
      });
    } finally {
      setSaving(false);
      setTimeout(() => setMessage({ text: "", type: "" }), 5000);
    }
  };

  // ✅ Row coloring
  const getRowClass = (status) => {
    const s = status?.trim() || "";
    if (s === "Pending" || s === "") return "row-pending";
    if (s === "Cancelled") return "row-cancelled";
    if (s === "Delivered") return "row-delivered";
    return "";
  };

  // ✅ Format date display
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return `${String(d.getDate()).padStart(2, "0")}-${String(
      d.getMonth() + 1
    ).padStart(2, "0")}-${d.getFullYear()}`;
  };

  return (
    <div className="container">
      <div className="admin-wrap">
        <div className="box-head" style={{ marginBottom: "5px" }}>
          <span>Update Deliveries</span>
        </div>

        <div className="controls">
          <p style={{ lineHeight: "10px" }}>Booking Date</p>
          <input
            type="date"
            value={reportDate}
            onChange={(e) => setReportDate(e.target.value)}
          />
          <button onClick={() => fetchBookings(true)} disabled={loading || saving}>
            {loading ? "Loading..." : "Load Bookings"}
          </button>
        </div>

        {/* ✅ Inline message */}
        {message.text && (
          <div
            className={`inline-message ${
              message.type === "success"
                ? "msg-success"
                : message.type === "error"
                ? "msg-error"
                : "msg-info"
            }`}
          >
            {message.text}
          </div>
        )}

        {bookings.length > 0 && (
          <div className="table-container">
            <table className="bookings-table">
              <thead>
                <tr>
                  <th>No</th>
                  <th>Villa No</th>
                  <th>Booked On</th>
                  <th>Status</th>
                  <th>Delivery Date</th>
                  <th>Remarks</th> {/* ✅ new column */}
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {bookings.map((b, i) => (
                  <tr
                    key={`${b.Villa_No}-${b.Booking_Day}-${b.Booking_No || "N"}`}
                    className={getRowClass(b.Delivery_Status)}
                  >
                    <td data-label="No">{b.Booking_No}</td>
                    <td data-label="Villa No">{b.Villa_No}</td>
                    <td data-label="Booked On">{formatDate(b.Booking_Day)}</td>
                    <td data-label="Status">
                      <select
                        value={b.Delivery_Status}
                        onChange={(e) =>
                          handleChange(i, "Delivery_Status", e.target.value)
                        }
                        className={`status-select ${getRowClass(
                          b.Delivery_Status
                        )}`}
                        disabled={saving}
                      >
                        <option value="">--Select--</option>
                        <option value="Pending">Pending</option>
                        <option value="Delivered">Delivered</option>
                        <option value="Cancelled">Cancelled</option>
                      </select>
                    </td>
                    <td data-label="Delivery Date">
                      <input
                        type="date"
                        value={b.Actual_Delivery_Date || ""}
                        onChange={(e) =>
                          handleChange(i, "Actual_Delivery_Date", e.target.value)
                        }
                        disabled={
                          saving ||
                          b.Delivery_Status === "Pending" ||
                          b.Delivery_Status === "" ||
                          b.Delivery_Status === null
                        }
                      />
                    </td>
                    <td data-label="Remarks">
                      <input
                        type="text"
                        value={b.Remarks || ""}
                        placeholder="Enter remarks"
                        onChange={(e) =>
                          handleChange(i, "Remarks", e.target.value)
                        }
                        maxLength={100}
                        disabled={saving}
                      />
                    </td>
                    <td data-label="Action">
                      <button
                        type="button"
                        onClick={(e) => handleUpdate(e, b)}
                        className="save-btn"
                        disabled={saving}
                      >
                        {saving ? "Saving..." : "Save"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default UpdateDelivery;
